/**
 * PGRST303 : PostgREST refuse les claims du jeton, avant toute exécution de la
 * requête.
 *
 * Constaté en prod (16-17/09/2026, 6 refus sur 4 comptes, Sentry 147643315) :
 * dans la seconde qui suit une connexion ou un renouvellement de jeton, une
 * instance PostgREST juge le nouveau jeton « émis dans le futur »
 * (`JWT issued at future`) — décalage d'horloge côté Supabase. Le refus tombe
 * requête par requête : le même jeton passe sur la requête voisine, quelques
 * millisecondes plus tard, et partout une seconde après.
 *
 * Sans reprise, ce refus se lisait selon l'appelant comme une panne (écran
 * « Impossible de charger votre espace »), comme une réponse vide (franchisé
 * sans magasin, fiche salarié absente → déconnexion) ou comme un échec
 * d'enregistrement.
 */
const JWT_CLAIMS_ERROR_CODE = "PGRST303";

export const JWT_CLAIMS_RETRY_DELAY_MS = 1000;

const isJwtClaimsRefusal = async (response: Response): Promise<boolean> => {
  if (response.status !== 401) return false;
  try {
    // `clone` : l'appelant doit pouvoir lire le corps de la réponse rendue.
    const body = (await response.clone().json()) as { code?: unknown } | null;
    return body?.code === JWT_CLAIMS_ERROR_CODE;
  } catch {
    return false;
  }
};

/**
 * `fetch` qui renvoie une fois, après `delayMs`, une requête refusée en PGRST303.
 *
 * Rejouer est sans risque, écritures comprises : PostgREST valide le jeton avant
 * d'ouvrir la transaction, rien n'a été exécuté. Le jeton est le même — il était
 * valide, seulement présenté trop tôt. Toute autre réponse, et celle de la
 * seconde tentative, est rendue telle quelle.
 */
export const createFetchWithJwtClaimsRetry =
  (
    baseFetch: typeof fetch = (input, init) => fetch(input, init),
    delayMs: number = JWT_CLAIMS_RETRY_DELAY_MS,
  ): typeof fetch =>
  async (input, init) => {
    const response = await baseFetch(input, init);
    if (!(await isJwtClaimsRefusal(response))) return response;
    // Un `Request` porte son propre corps, consommé par le premier envoi ;
    // supabase-js passe toujours une URL et un `init`.
    if (typeof Request !== "undefined" && input instanceof Request) return response;
    if (init?.signal?.aborted) return response;
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    return baseFetch(input, init);
  };

export const fetchWithJwtClaimsRetry = createFetchWithJwtClaimsRetry();
